import { ServicePlayer, type PlayerDevice } from "./service_player";
import { sleep } from "../util";
import type { CurrentTrackItem, RepeatMode } from "./types";
import type CMSConnector from "../connector";

class MockClient {
  tracks: CurrentTrackItem[];
  currentTrackIndex: number;

  constructor() {
    this.tracks = [];
    this.currentTrackIndex = -1;
  }

  get currentTrack(): CurrentTrackItem | null {
    return this.tracks[this.currentTrackIndex] || null;
  }

  nextTrack(): CurrentTrackItem | null {
    // TOOD: implement repeat
    this.currentTrackIndex = Math.min(
      this.currentTrackIndex + 1,
      this.tracks.length - 1
    );
    return this.currentTrack;
  }

  previousTrack(): CurrentTrackItem | null {
    this.currentTrackIndex = Math.max(this.currentTrackIndex - 1, 0);
    return this.currentTrack;
  }

  switchToTrack(idx: number): CurrentTrackItem | null {
    this.currentTrackIndex = Math.min(idx, this.tracks.length - 1);
    return this.currentTrack;
  }

  static fromAlbum(album: SpotifyApi.AlbumObjectFull): MockClient {
    const trackList: CurrentTrackItem[] = [];
    for (const track of album.tracks.items) {
      trackList.push({
        name: track.name,
        artists: track.artists.map((a) => ({ name: a.name })),
        album: { name: album.name },
        durationMs: track.duration_ms,
        id: track.id,
        image: album.images[0]?.url || "",
      });
    }
    const client = new MockClient();
    client.tracks = trackList;
    return client;
  }

  static fromPlaylist(playlist: SpotifyApi.PlaylistObjectFull): MockClient {
    const trackList: CurrentTrackItem[] = [];
    for (const plTrack of playlist.tracks.items) {
      const track = plTrack.track;
      if (!track) {
        continue;
      }
      trackList.push({
        name: track.name,
        artists: track.artists.map((a) => ({ name: a.name })),
        album: { name: track.album.name },
        durationMs: track.duration_ms,
        id: track.id,
        image: track.album.images[0]?.url || "",
      });
    }
    const client = new MockClient();
    client.tracks = trackList;
    return client;
  }

  static fromTracks(tracks: SpotifyApi.TrackObjectFull[]): MockClient {
    const trackList: CurrentTrackItem[] = [];
    for (const track of tracks) {
      trackList.push({
        name: track.name,
        artists: track.artists.map((a) => ({ name: a.name })),
        album: { name: track.album.name },
        durationMs: track.duration_ms,
        id: track.id,
        image: track.album.images[0]?.url || "",
      });
    }
    const client = new MockClient();
    client.tracks = trackList;
    return client;
  }

  static fromTrack(track: SpotifyApi.TrackObjectFull): MockClient {
    const client = new MockClient();
    client.tracks = [
      {
        name: track.name,
        artists: track.artists.map((a) => ({ name: a.name })),
        album: { name: track.album.name },
        durationMs: track.duration_ms,
        id: track.id,
        image: track.album.images[0]?.url || "",
      },
    ];
    return client;
  }
}

export class MockPlayer extends ServicePlayer {
  service = "mock" as const;
  supportsDevices = true;
  cms: CMSConnector;
  client: MockClient;

  player!: Spotify.Player;
  deviceId = "";
  currentDeviceId = "";

  constructor(cms: CMSConnector) {
    super();
    this.cms = cms;
    this.client = new MockClient();
  }

  async init() {
    this.progressTimer();
    console.debug("Mock player init done");
  }

  private async progressTimer() {
    // Keep incrementing progressMs of the playback state
    let lastUpdate = +new Date();
    while (true) {
      await sleep(1000);
      const now = +new Date();
      const elapsed = now - lastUpdate;
      lastUpdate = now;
      if (this.playbackState.isPaused || !this.playbackState.currentTrack) {
        continue;
      }
      let newProgress = this.playbackState.progressMs + elapsed;
      if (newProgress >= this.playbackState.currentTrack.durationMs) {
        this.client.nextTrack();
        newProgress = 0;
      }
      this.playbackState.progressMs = newProgress;
      this.dispatchState();
    }
  }

  dispatchState() {
    this.playbackState.currentTrack = this.client.currentTrack;
    if (!this.playbackState.currentTrack) {
      this.playbackState.isPaused = true;
    }
    document.dispatchEvent(
      new CustomEvent("umg:player:playbackStateChanged", {
        detail: JSON.parse(JSON.stringify(this.playbackState)),
      })
    );
    console.debug("Mock player state", this.playbackState);
  }

  public async pause() {
    this.playbackState.isPaused = true;
    this.dispatchState();
  }

  public async resume() {
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  public async seek(positionMs: number) {
    this.playbackState.progressMs = positionMs;
    this.dispatchState();
  }

  public async togglePlayback(shuffle?: boolean) {
    this.playbackState.isShuffle = shuffle || false;
    this.dispatchState();
  }

  async setRepeatMode(mode: RepeatMode) {
    this.playbackState.repeatMode = mode;
    this.dispatchState();
  }

  public async previousTrack() {
    this.client.previousTrack();
    this.playbackState.progressMs = 0;
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  public async nextTrack() {
    this.client.nextTrack();
    this.playbackState.progressMs = 0;
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  public async getDevices(): Promise<PlayerDevice[]> {
    return [];
  }

  public async setDevice(deviceId: string): Promise<void> {
    this.currentDeviceId = deviceId;
    this.dispatchState();
  }

  public async playPlaylist(
    playlistId: string,
    options?: { position?: number; deviceId?: string }
  ): Promise<void> {
    const playlist = await this.cms.api.spotify.getPlaylist(playlistId);
    this.client = MockClient.fromPlaylist(playlist);
    this.client.switchToTrack(options?.position || 0);
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  public async playAlbum(
    albumId: string,
    options?: { position?: number; deviceId?: string }
  ): Promise<void> {
    const album = await this.cms.api.spotify.getAlbum(albumId);
    this.client = MockClient.fromAlbum(album);
    this.client.switchToTrack(options?.position || 0);
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  public async playTrack(trackId: string, options = {}): Promise<void> {
    const track = await this.cms.api.spotify.getTrack(trackId);
    this.client = MockClient.fromTrack(track);
    this.client.switchToTrack(0);
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  public async playTracks(trackIds: string[], options = {}): Promise<void> {
    const tracks = await this.cms.api.spotify.getSeveralTracks(trackIds);
    this.client = MockClient.fromTracks(tracks);
    this.client.switchToTrack(0);
    this.playbackState.isPaused = false;
    this.dispatchState();
  }

  async enqueueTracks(_: string[]): Promise<void> {
    // Spotify API has very poor queue management, so this is not supported for now
  }

  async setVolume(volume: number): Promise<void> {
    this.playbackState.volume = volume;
    this.dispatchState();
  }
}
